import axios from "axios";
import { useEffect, useState } from "react";
import { ApplicationJsonConfigHeader, BaseUrl } from "../../Api/axios.config";
import InputFull from "../../Components/Common/InuptFull";
import Menu from "../../Components/Template/Menu";
import Template from "../../Components/Template/Template";
import { validateInput } from "../../Components/Utils/ValidationUtils";
import { getProfile } from "../StudentProfile/ProfileApi";
import { ErrorMsgToaster, SuccessMsgToaster } from "../../Components/Utils/Toaster";
import { StudentProfile } from "../../Utils/types";
import { useLocation } from "react-router-dom";

// ======================== CONSTANTS ========================
const checkoutURL = `${BaseUrl}/checkout/checkoutComplete`;
const paymentFailureUrl = `${BaseUrl}/paymentFailure`;
const paymentSuccessUrl = `${BaseUrl}/paymentSuccess`;


// ======================== LOCATION TYPE ========================
type LocationState = { page?: string };

// ======================== CHECKOUT PAGE ========================
const Checkout = () => {
  const location = useLocation() as unknown as { state: LocationState };
  const page = location?.state?.page ?? "cart";

  // Store redirection type locally
  localStorage.setItem("pageData", page);

  useEffect(() => {
    // TEMPORARILY DISABLED FOR TESTING - Uncomment after testing is complete
    // This was causing issues during development/testing by clearing orders on refresh
    /*
    // Check if this is an actual page refresh (user pressed F5 or refresh button)
    // We use a session flag to differentiate between first navigation and refresh
    const checkoutVisited = sessionStorage.getItem("checkoutVisited");

    if (performance?.navigation?.type === 1 && checkoutVisited === "true") {
      // This is a refresh after already visiting checkout - treat as payment failure
      console.log("Page refreshed - treating as payment failure");
      handlePaymentFailure();
    } else {
      // First visit to checkout page - mark it as visited
      console.log("First visit to checkout page");
      sessionStorage.setItem("checkoutVisited", "true");
    }
    */
    console.log("⚠️ Payment failure handler temporarily disabled for testing");
  }, []);

  return (
    <Template
      menu={<Menu selectedItem={``} />}
      body={<Checkoutbody />}
      submenu={<SubMenu />}
    />
  );
};

// ======================== PAYMENT FAILURE ========================
function handlePaymentFailure() {
  const cartOrderId = localStorage.getItem("orderId");
  const pageData = localStorage.getItem("pageData");

  axios
    .post(
      paymentFailureUrl,
      { orderId: cartOrderId },
      {
        headers: { ...ApplicationJsonConfigHeader, authorization: `Bearer ${localStorage.getItem("token")}` },
      }
    )
    .then((res) => {
      ErrorMsgToaster(res.data);
      // Clear checkout session flag on failure
      sessionStorage.removeItem("checkoutVisited");
      window.location.replace(pageData === "enroll" ? "/courses" : "/mycart");
    })
    .catch(() => {
      ErrorMsgToaster("Payment failed, please try again.");
      sessionStorage.removeItem("checkoutVisited");
    });
}

// ======================== LOAD RAZORPAY SCRIPT ========================
function loadScript(src: string) {
  return new Promise<boolean>((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// ======================== HANDLE PAYMENT ========================
function handlePaymentMethod(result: any) {
  const { amount, order_id, currency, email, firstName, mobileNumber, address } = result.data;

  const options = {
    key: "rzp_live_8w90ON9MzUXKp9", // LIVE Razorpay Key
    amount: amount.toString(),
    currency,
    name: "Her SpringBoard",
    description: "Transaction",
    order_id,
    handler: async function (response: any) {
      const data = {
        orderCreationId: order_id,
        razorpayPaymentId: response.razorpay_payment_id,
        razorpayOrderId: response.razorpay_order_id,
        razorpaySignature: response.razorpay_signature,
        orderId: localStorage.getItem("orderId"),
      };

      axios
        .post(paymentSuccessUrl, data, {
          headers: {
            authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        })
        .then((res) => {
          SuccessMsgToaster(res.data);
          // Clear checkout session flag on success
          sessionStorage.removeItem("checkoutVisited");
          window.location.replace("/myCourses");
        })
        .catch(() => ErrorMsgToaster("Payment verification failed!"));
    },
    modal: {
      ondismiss: function () {
        console.log("Checkout closed");
        handlePaymentFailure();
      },
    },
    prefill: { name: firstName, email, contact: mobileNumber },
    notes: { address },
    theme: { color: "#61dafb" },
  };

  const paymentObject = new (window as any).Razorpay(options);
  paymentObject.open();
}

// ======================== DISPLAY RAZORPAY ========================
async function displayRazorpay(studentDetail: StudentProfile | undefined) {
  console.log("displayRazorpay called with studentDetail:", studentDetail);

  if (!studentDetail) {
    console.error("No student detail available");
    return ErrorMsgToaster("Please wait, loading profile...");
  }

  console.log("Checking for orderId in localStorage...");
  const cartOrderId = localStorage.getItem("orderId");
  console.log("OrderId found:", cartOrderId);

  if (!cartOrderId) {
    console.error("No orderId found in localStorage!");
    console.log("All localStorage keys:", Object.keys(localStorage));
    return ErrorMsgToaster("⚠ Order not created! Please go back to cart and try again.");
  }

  const detail = { ...studentDetail, orderId: cartOrderId };
  console.log("Proceeding with checkout details:", detail);

  console.log("Loading Razorpay script...");
  const loaded = await loadScript("https://checkout.razorpay.com/v1/checkout.js");

  if (!loaded) {
    console.error("Failed to load Razorpay script");
    return alert("⚠ Unable to load payment gateway. Check your internet.");
  }

  console.log("Razorpay script loaded successfully");

  const token = localStorage.getItem("token");
  console.log("Making request to checkout API...");

  axios
    .post(checkoutURL, detail, {
      headers: { ...ApplicationJsonConfigHeader, authorization: `Bearer ${token}` },
    })
    .then((response) => {
      console.log("Checkout API response:", response.data);
      handlePaymentMethod(response);
    })
    .catch((err) => {
      console.error("Checkout API error:", err);
      console.error("Error response:", err.response?.data);
      ErrorMsgToaster(err.response?.data?.error ?? "Checkout Error");
    });
}

// ======================== CHECKOUT BODY ========================
const Checkoutbody = () => {
  const [studentDetail, setStudentDetail] = useState<StudentProfile>();
  const [firstNameErr, setFirstNameErr] = useState("");
  const [lastNameErr, setLastNameErr] = useState("");
  const [emailErr, setEmailErr] = useState("");

  useEffect(() => {
    getProfile("Student").then((data) => setStudentDetail(data));
  }, []);

  const updateField = (field: Record<string, any>) => {
    setStudentDetail((prev) => ({
      ...prev,
      ...field,
    }));
  };

  return (
    <div className="flex flex-col text-gray-700">
      <div className="-mt-10 text-lp_blue text-2xl font-bold">Checkout</div>

      <div className="grid grid-cols-2 gap-6 w-full mt-8">
        <div className="mt-2">
          <InputFull
            type="text"
            label="First Name"
            value={studentDetail?.firstName ?? ""}
            onChange={(e) => updateField({ firstName: e.target.value })}
            errCls={firstNameErr ? "border-red-400" : "border-gray-400"}
            onBlur={(e) => setFirstNameErr(validateInput("name", e.target.value))}
          />
        </div>

        <div className="mt-2">
          <InputFull
            type="text"
            label="Last Name"
            value={studentDetail?.lastName ?? ""}
            onChange={(e) => updateField({ lastName: e.target.value })}
            errCls={lastNameErr ? "border-red-400" : "border-gray-400"}
            onBlur={(e) => setLastNameErr(validateInput("name", e.target.value))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 w-full mt-2">
        <div className="mt-2">
          <InputFull
            type="email"
            label="Email"
            value={studentDetail?.email ?? ""}
            onChange={(e) => updateField({ email: e.target.value })}
            errCls={emailErr ? "border-red-400" : "border-gray-400"}
            onBlur={(e) => setEmailErr(validateInput("email", e.target.value))}
          />
        </div>

        <div className="mt-2">
          <InputFull
            type="tel"
            label="Mobile"
            value={studentDetail?.mobileNumber ?? ""}
            maxLength={10}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/\D/g, "");
              updateField({ mobileNumber: onlyNums });
            }}
          />
        </div>
      </div>

      <button
        className="bg-lp_blue text-white text-xl h-10 w-32 rounded-md font-Lato font-bold place-self-end mt-8 hover:bg-blue-700"
        type="button"
        disabled={!studentDetail}
        onClick={() => {
          console.log("CHECKOUT CLICKED 🚀");
          displayRazorpay(studentDetail);
        }}
      >
        CHECKOUT
      </button>
    </div>
  );
};

// ======================== EMPTY SUBMENU ========================
const SubMenu = () => <></>;
export default Checkout;
